/**
 * Scratch — keep large tool/subagent outputs OUT of the main context, but queryable AS FILES.
 *
 * Pattern: many-tool / many-subagent engines bloat context with raw outputs (search results, big
 * file reads, subagent reports) that are mostly noise once a gist is taken. Here, a tool wrapped with
 * `Scratch.capture` writes any oversized result to an ephemeral scratch filesystem (a real VFS) and
 * returns a compact stub (path + preview) to context. To recover a buried detail later you do NOT
 * re-bloat context — you peek with the FILE tools already on hand:
 *   • Grep/Glob/Read over the scratch dir — returns matching lines, not the blob (a free, light peek).
 *   • Ask({question, over?}) — a CHEAP child agent over the scratch FS that greps/reads and returns
 *     just the answer; its reasoning tokens never touch the caller's context.
 * Two-tier division of labour: cheap RETRIEVE/EXTRACT, strong caller SYNTHESIZE.
 *
 * The scratch FS is injected (IoC): pass a MemFilesystem for ephemeral, or a disk-backed one under the
 * session dir to persist across resume (CC's lesson: the filesystem is the durable store). Segregated
 * module — zero Agent-core changes.
 */
import type { IFilesystem } from '@livx.cc/wcli/core';
import type { ChatLike } from './llm';
import type { AgentTool } from './tools';
import { toolsByName } from './tools';
import { Agent } from './Agent';
import { mkdirp } from './tools.structured';
import { forComponent } from './logging';

const log = forComponent('scratch');

/** Default scratch dir (a path in the injected scratch FS). */
export const SCRATCH_DIR = '/scratch';

function shortArgs(args: any): string {
  try { const s = JSON.stringify(args ?? {}); return s.length > 50 ? s.slice(0, 47) + '…' : s; } catch { return ''; }
}
const slug = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 32) || 'out';

export interface ScratchOptions {
  /** Min result length (chars) to capture; smaller results pass through untouched. Default 1500. */
  threshold?: number;
  /** Dir within the scratch FS to write captures. Default SCRATCH_DIR. */
  dir?: string;
  /** Chars of the captured output to preview in the stub. Default 320. */
  previewChars?: number;
}

/**
 * Owns an (injected) scratch filesystem + a capture counter. `capture(tool)` wraps a tool so its
 * oversized string results spill to a scratch file and are replaced in context with a stub.
 */
export class Scratch {
  public options: Required<ScratchOptions>;
  private seq = 0;
  private dirReady?: Promise<void>;
  constructor(public fs: IFilesystem, options: ScratchOptions = {}) {
    this.options = { threshold: 1500, dir: SCRATCH_DIR, previewChars: 320, ...options };
  }

  /** Number of captures so far. */
  get count(): number { return this.seq; }

  /** Wrap a tool: oversized STRING results spill to a scratch file; everything else passes through. */
  capture(tool: AgentTool): AgentTool {
    const { threshold, dir, previewChars } = this.options;
    return {
      ...tool,
      run: async (args, ctx) => {
        const raw = await tool.run(args, ctx);
        if (typeof raw !== 'string' || raw.length <= threshold) return raw;
        const id = 'a' + ++this.seq;
        const path = `${dir}/${id}-${slug(tool.name)}.txt`;
        const header = `# ${tool.name}(${shortArgs(args)}) — ${raw.length} bytes\n`;
        try { await (this.dirReady ??= mkdirp(this.fs, dir)); await this.fs.writeFile(path, header + raw); }
        catch (e) { log.debug('scratch write failed; returning raw', e); return raw; } // never lose data on FS error
        const preview = raw.slice(0, previewChars).replace(/\s+/g, ' ').trim();
        return `[scratch ${path} · ${tool.name} · ${raw.length} bytes — full output saved out of context to keep it clean]\n` +
          `preview: ${preview}…\n` +
          `To pull a specific detail, Grep/Read ${path}, or call Ask({ question: "…", over: "${path}" }). Do NOT guess at what the preview cuts off.`;
      },
    };
  }

  /** Wrap many tools at once. */
  captureAll(tools: AgentTool[]): AgentTool[] { return tools.map((t) => this.capture(t)); }

  /**
   * Spill an oversized tool result to a scratch file and return PAGE 1 + a recoverable, paginated stub.
   * Drop-in for `Agent.capToolResult`: the agent sees usable content immediately and knows how to get
   * the rest (refine the query, Read the file in pages with offset/limit, or Ask to extract specifics).
   * Lossless — unlike a plain crop, the full output stays available on the scratch FS.
   */
  async spill(full: string, info: { tool: string; args: any }, pageBytes = 8000): Promise<string> {
    const { dir } = this.options;
    const id = 'a' + ++this.seq;
    const path = `${dir}/${id}-${slug(info.tool)}.txt`;
    const header = `# ${info.tool}(${shortArgs(info.args)}) — ${full.length} bytes\n`;
    try { await (this.dirReady ??= mkdirp(this.fs, dir)); await this.fs.writeFile(path, header + full); }
    catch (e) { log.debug('scratch spill failed; cropping lossy', e); return full.slice(0, pageBytes) + `\n\n[output cropped to ${pageBytes} of ${full.length} bytes; full output unavailable (scratch write failed) — refine your query]`; }
    const head = full.slice(0, pageBytes);
    const nl = head.lastIndexOf('\n');
    const page = nl > pageBytes * 0.5 ? head.slice(0, nl) : head;
    return `${page}\n\n[output cropped — page 1 (${page.length} of ${full.length} bytes). Full output saved to ${path}. ` +
      `To see more: refine your query/command to narrow it, or Read ${path} with offset/limit to page through it, or Ask({ question: "…", over: "${path}" }) to extract specifics.]`;
  }
}

export interface AskOptions {
  /** The scratch filesystem to peek into (dedicated VFS holding only scratch files). */
  fs: IFilesystem;
  ai: ChatLike;
  /** Model for the peek — intentionally a CHEAP model; the caller synthesizes. */
  model: string;
  /** Step budget for the peek child (default 6). */
  maxSteps?: number;
  /** Scratch dir to search when `over` is omitted. Default SCRATCH_DIR. */
  dir?: string;
}

const ASK_PROMPT =
  'You are a retrieval-extraction step with Read, Grep and Glob over a scratch filesystem holding raw ' +
  'outputs from earlier tools. Find the information that answers the question and return it concisely, ' +
  'quoting values/facts verbatim. Do NOT add analysis or anything not grounded in the files. ' +
  'If the answer is not present, say so plainly.';

/**
 * `Ask` — peek into the scratch FS to answer a question WITHOUT loading raw data into the caller's
 * context. Spawns a cheap child agent (Read/Grep/Glob over scratch) that greps/reads and returns just
 * the answer. Pass `over` (a path) for a targeted peek, or omit to grep-discover.
 */
export function makeAskTool(o: AskOptions): AgentTool {
  const dir = o.dir ?? SCRATCH_DIR;
  return {
    name: 'Ask',
    description:
      'Answer a question by peeking into the scratch files — large earlier outputs (web search, big reads, subagent reports) kept out of your context. ' +
      'Pass `over` with a scratch path for a targeted lookup, or omit it to search the scratch dir. ' +
      'Returns only the extracted answer; the full data never enters your context.',
    parameters: {
      type: 'object',
      required: ['question'],
      properties: {
        question: { type: 'string', description: 'what you need from the scratch files' },
        over: { type: 'string', description: 'scratch path to read, e.g. "/scratch/a3-websearch.txt"; omit to search' },
      },
    },
    async run({ question, over }) {
      const q = String(question ?? '').trim();
      if (!q) return 'Error: empty question';
      const child = new Agent({
        ai: o.ai,
        model: o.model,
        fs: o.fs,
        tools: toolsByName(['Read', 'Grep', 'Glob']),
        maxSteps: o.maxSteps ?? 6,
        systemPrompt: ASK_PROMPT,
      });
      const hint = over ? `Start by reading: ${over}.` : `Grep/Glob ${dir} to find the relevant file(s) first.`;
      try {
        const res = await child.run(`${hint}\n\nQuestion: ${q}`);
        const answer = (res.text ?? '').trim();
        return answer || '(no answer found in scratch)';
      } catch (e: any) {
        log.debug('Ask peek failed', e);
        return `Error querying scratch: ${e?.message ?? e}`;
      }
    },
  };
}
